import { useEffect, useRef } from "react";
import { isIgnorablePlayError, useMediaElementEvents, type PlayerCallbacks } from "./mediaEvents.js";

/**
 * Plain `<audio>` for MP3 / AAC streams. Also honors `preload="none"` so
 * we don't fire off requests to a bunch of radio streams the moment the
 * list mounts.
 */
export function AudioPlayer({
  src,
  autoPlay = true,
  paused = false,
  controls = true,
  ...cb
}: {
  src: string;
  autoPlay?: boolean;
  paused?: boolean;
  /** Native browser controls. Off on TV — a D-pad can't reach them (see PlayerControls). */
  controls?: boolean;
} & PlayerCallbacks) {
  const ref = useRef<HTMLAudioElement>(null);
  useMediaElementEvents(ref, cb, src);

  // Real pause/resume (e.g. from the MediaSession notification).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (paused) el.pause();
    else
      void el.play().catch((err) => {
        if (!isIgnorablePlayError(err)) cb.onFatal?.({ message: String(err?.message ?? err), network: false });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  return (
    <audio
      ref={ref}
      controls={controls}
      src={src}
      autoPlay={autoPlay}
      preload="none"
      className="w-full"
    />
  );
}
